import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Search, RefreshCw, Eye, CheckCircle } from 'lucide-react';
import SiparisDetayModal from '../production/SiparisDetayModal';
import { BaskiGorselComponent } from '../production/BaskiGorselLazyImage';
import { customerPortalService } from '../../services/customerPortalApi';
import toast from 'react-hot-toast';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import config from '../../config/config';

/**
 * DigitalPrintingTamamlananSiparislerTab - Müşteri Portalı Tamamlanan Siparişler Tabı
 * Sadece o müşterinin tamamlanan siparişlerini gösterir
 * Sadece görüntüleme modu
 */
const DigitalPrintingTamamlananSiparislerTab = () => {
  const { customer } = useCustomerAuth();
  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    toplam: 0,
    toplamMetre: 0
  });
  
  // Sipariş detay modalı state'leri
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSiparis, setSelectedSiparis] = useState(null);

  // Siparişleri yükle
  const loadSiparisler = useCallback(async () => {
    if (!customer?.id) {
      return;
    }

    setLoading(true);
    try {
      const response = await customerPortalService.get('/digital-printing/tamamlanan-siparisler');
      if (response.data.success) {
        const filteredData = response.data.data || [];
        
        // Varyantları düzleştir
        const variants = filteredData.flatMap(siparis => 
          (siparis.varyantlar || [])
            .filter(varyant => varyant.durum === 'Tamamlandı')
            .map(varyant => ({ siparis, varyant }))
        );
        
        setSiparisler(variants);
        
        // İstatistikleri hesapla
        const toplam = variants.length;
        const toplamMetre = variants.reduce((sum, x) => sum + parseFloat(x.varyant.metre || 0), 0);
        
        setStats({ toplam, toplamMetre });
      }
    } catch (error) {
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [customer?.id]);

  // Component mount olduğunda verileri yükle
  useEffect(() => {
    loadSiparisler();
  }, [loadSiparisler]);

  // Sipariş detayını göster
  const handleShowDetail = (item) => {
    const { siparis, varyant } = item;
    
    const siparisData = {
      ...siparis,
      varyantlar: siparis.varyantlar || [varyant]
    };
    
    setSelectedSiparis(siparisData);
    setShowDetailModal(true);
  };

  // Arama değiştir
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filtrelenmiş siparişler
  const filteredSiparisler = siparisler.filter(item => {
    // Arama filtresi
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const siparisNo = (item.siparis.siparis_no || '').toLowerCase();
      const desenNo = (item.varyant.desen?.desen_no || '').toLowerCase();
      const musteriAdi = (item.siparis.musteri_adi || '').toLowerCase();
      
      if (!siparisNo.includes(searchLower) && 
          !desenNo.includes(searchLower) && 
          !musteriAdi.includes(searchLower)) {
        return false;
      }
    }
    
    return true;
  });

  // Görsel URL'lerini işle - Modal'daki processImageUrl mantığıyla aynı
  const processImageUrl = (gorselUrls) => {
    if (!gorselUrls) return null;
    
    let urls = gorselUrls;
    if (typeof urls === 'string') {
      try {
        urls = JSON.parse(urls);
      } catch (e) {
        urls = [urls];
      }
    }
    
    if (Array.isArray(urls) && urls.length > 0) {
      const first = urls[0];
      const cleanUrl = String(first).replace(/^["']+|["']+$/g, '');
      return cleanUrl.startsWith('http') ? cleanUrl : `${config.backendUrl}${cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`}`;
    }
    
    return null;
  };

  return (
    <div className="space-y-4 md:space-y-6 -mx-3 sm:-mx-4 lg:-mx-6 px-3 sm:px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
        <div>
          <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">Tamamlanan Siparişler</h2>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">Tamamlanmış siparişleriniz</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button 
            onClick={loadSiparisler}
            className="inline-flex items-center justify-center px-4 py-2.5 sm:py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 sm:h-5 sm:w-5 ${loading ? 'animate-spin' : ''}`} />
            <span className="ml-2">Yenile</span>
          </button>
        </div>
      </div>

      {/* Summary Card */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3 lg:gap-4">
        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border border-green-200 dark:border-green-700 p-2.5 sm:p-3 rounded-lg sm:rounded-xl shadow-sm hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide mb-1.5">Toplam</p>
              <p className="text-xl sm:text-2xl font-bold text-green-900 dark:text-green-100">{stats.toplam}</p>
              <p className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 mt-1">{stats.toplamMetre.toFixed(1)} m</p>
            </div>
            <div className="bg-green-100 dark:bg-green-800/30 p-2 sm:p-3 rounded-lg">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Arama */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Sipariş no, desen no, müşteri ara..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sipariş Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw className="h-12 w-12 text-gray-400 dark:text-gray-500 animate-spin" />
            <p className="mt-4 text-gray-500 dark:text-gray-400">Siparişler yükleniyor...</p>
          </div>
        ) : filteredSiparisler.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            <p className="mt-2 text-gray-500 dark:text-gray-400">Henüz tamamlanan sipariş bulunmuyor</p>
          </div>
        ) : (
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {filteredSiparisler.map((item) => {
                const { siparis, varyant } = item;
                
                // Görsel URL'lerini al - Modal'daki mantıkla aynı
                let imageUrl = null;
                
                // Önce varyant.desen'den resim al
                if (varyant.desen?.ozel_gorsel_url) {
                  imageUrl = varyant.desen.ozel_gorsel_url.startsWith('http') 
                    ? varyant.desen.ozel_gorsel_url 
                    : `${config.backendUrl}${varyant.desen.ozel_gorsel_url.startsWith('/') ? varyant.desen.ozel_gorsel_url : `/${varyant.desen.ozel_gorsel_url}`}`;
                } else if (varyant.desen?.gorsel_urls) {
                  imageUrl = processImageUrl(varyant.desen.gorsel_urls);
                }
                
                // Varyant.desen'de resim yoksa, siparis.gorsel_urls'e bak (fallback)
                if (!imageUrl && siparis.gorsel_urls) {
                  imageUrl = processImageUrl(siparis.gorsel_urls);
                }

                return (
                  <div
                    key={`${siparis.siparis_no}-${varyant.id}`}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 bg-green-50 dark:bg-green-900/20"
                  >
                    {/* Görsel */}
                    <div
                      className="relative h-48 bg-gray-100 dark:bg-gray-700 rounded-t-lg overflow-hidden cursor-pointer"
                      onClick={() => handleShowDetail(item)}
                    >
                      {imageUrl ? (
                        <BaskiGorselComponent
                          imageUrl={imageUrl}
                          alt={varyant.desen?.desen_no || siparis.siparis_no || 'Varyant'}
                          useThumbnail
                          priority={false}
                        />
                      ) : null}
                      {/* Fallback for no image */}
                      <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500" style={{ display: imageUrl ? 'none' : 'flex' }}>
                        <div className="text-center">
                          <FileText className="h-16 w-16 mx-auto mb-2" />
                          <p className="text-sm">Resim Yok</p>
                        </div>
                      </div>

                      {/* Durum Badge - Sağ Üst */}
                      <div className="absolute top-2 right-2 z-20">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                          Tamamlandı
                        </span>
                      </div>
                    </div>
                    
                    {/* Bilgiler */}
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-2.5 text-xs">
                        <div className="col-span-2 flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {siparis.siparis_no}
                          </h4>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(siparis.siparis_tarihi).toLocaleDateString('tr-TR')}
                          </span>
                        </div>
                        <div className="col-span-2 flex items-center justify-between">
                          <p className="text-gray-500 dark:text-gray-400 truncate">
                            {varyant.desen?.desen_no || 'Varyant'}
                          </p>
                          <p className="text-gray-500 dark:text-gray-400 text-right text-xs ml-2 truncate">
                            {siparis.musteri_adi || 'Müşteri yok'}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-500 dark:text-gray-400">
                            {Math.round(parseFloat(varyant.metre || 0))} m
                          </p>
                        </div>
                      </div>
                      
                      {/* Detayları Görüntüle Butonu */}
                      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => handleShowDetail(item)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                          <span>Detayları Görüntüle</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sipariş Detay Modal */}
      {selectedSiparis && (
        <SiparisDetayModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedSiparis(null);
          }}
          siparis={selectedSiparis}
          isPublicPage={true}
        />
      )}
    </div>
  );
};

export default DigitalPrintingTamamlananSiparislerTab;
